<template>
  <v-main>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>
      <div class="jv-root">
        <!-- Page Header -->
        <div class="jv-page-header">
          <div class="d-flex align-center">
            <div class="jv-header-icon-wrap mr-3">
              <v-icon color="white" size="24">mdi-file-tree-outline</v-icon>
            </div>
            <div>
              <div class="white--text text-h6 font-weight-bold">
                {{ $tc('sac.jsonTitle', 2) }}
              </div>
              <div class="white--text text-caption" style="opacity: 0.75;">
                Define your CAE structure using JSON — the graph updates in real time
              </div>
            </div>
          </div>
          <v-chip class="jv-status-chip" small>
            <v-icon left x-small>mdi-circle</v-icon>
            Live Preview
          </v-chip>
        </div>

        <!-- Main Layout -->
        <div class="jv-layout">
          <!-- Left Panel: JSON Editor -->
          <div class="jv-left-panel">
            <div class="jv-panel-header">
              <v-icon color="white" size="16" class="mr-2">mdi-code-braces</v-icon>
              <span class="white--text text-caption font-weight-bold">JSON Editor</span>
              <v-spacer />
              <v-btn
                x-small
                depressed
                color="rgba(255,255,255,0.22)"
                dark
                class="mr-1"
                style="text-transform: none; font-weight: 600; letter-spacing: 0.02em; border-radius: 6px;"
                @click="loadTemplate"
              >
                <v-icon left size="14">mdi-folder-open-outline</v-icon>
                Templates
              </v-btn>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    color="white"
                    v-bind="attrs"
                    v-on="on"
                    @click="loadExampleTemplate"
                  >
                    <v-icon size="16">mdi-code-tags</v-icon>
                  </v-btn>
                </template>
                <span>Load example template</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon x-small color="white" v-bind="attrs" v-on="on" @click="formatJson">
                    <v-icon size="16">mdi-auto-fix</v-icon>
                  </v-btn>
                </template>
                <span>Format JSON</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon x-small color="white" v-bind="attrs" v-on="on" @click="clearJson">
                    <v-icon size="16">mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span>Clear</span>
              </v-tooltip>
            </div>

            <!-- Validation Banner -->
            <div v-if="jsonInput && !isJsonValid" class="jv-error-banner">
              <v-icon color="error" size="16" class="mr-1">mdi-alert-circle-outline</v-icon>
              <span class="text-caption">Invalid JSON — fix errors before creating a CAE</span>
            </div>
            <div v-if="jsonInput && isJsonValid" class="jv-valid-banner">
              <v-icon color="success" size="16" class="mr-1">mdi-check-circle-outline</v-icon>
              <span class="text-caption">Valid JSON — ready to create</span>
            </div>

            <textarea
              v-model="jsonInput"
              class="jv-editor"
              placeholder='Paste your CAE JSON here, or click "Load Template" above...'
              @input="onEditorInput"
              spellcheck="false"
            ></textarea>

            <!-- Action Bar -->
            <div class="jv-action-bar">
              <v-btn outlined small color="grey darken-1" @click="$router.go(-1)" class="jv-btn">
                <v-icon left small>mdi-arrow-left</v-icon> Back
              </v-btn>
              <v-spacer />
              <v-btn
                color="primary"
                small
                :disabled="!isJsonValid"
                @click="handleCreateClick(jsonInput)"
                class="jv-btn"
                elevation="0"
              >
                <v-icon left small>mdi-plus-circle-outline</v-icon> Create CAE
              </v-btn>
            </div>
          </div>

          <!-- Right Panel: JSONCrack Viewer -->
          <div class="jv-right-panel">
            <div class="jv-panel-header">
              <v-icon color="white" size="16" class="mr-2">mdi-graph</v-icon>
              <span class="white--text text-caption font-weight-bold">Visual Graph</span>
              <v-spacer />
              <v-chip x-small color="rgba(255,255,255,0.2)" text-color="white">
                JSONCrack
              </v-chip>
            </div>
            <div class="jv-iframe-wrap">
              <iframe
                id="jsoncrackEmbed"
                src="http://localhost:8888/widget"
                width="100%"
                height="100%"
                class="jv-iframe"
                @load="sendToEmbed"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </v-container>

    <!-- ── Load Template Dialog ── -->
    <v-dialog v-model="templateDialog" max-width="560">
      <v-card style="border-radius: 14px; overflow: hidden;">
        <div
          style="background: linear-gradient(135deg, #1a237e, #283593); padding: 16px 22px; display: flex; align-items: center; color: white;"
        >
          <v-icon color="white" class="mr-2">mdi-folder-open-outline</v-icon>
          <span style="font-weight: 700; font-size: 1rem;">Load Saved Template</span>
          <v-spacer />
          <v-btn icon small color="white" @click="templateDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </div>
        <v-card-text class="pa-4">
          <div v-if="loadingTemplates" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else-if="templates.length === 0" class="text-center py-8 grey--text">
            <v-icon size="48" color="grey lighten-2">mdi-file-outline</v-icon>
            <div class="mt-2 text-caption">
              No saved templates yet. Create one in the Template Editor.
            </div>
            <v-btn small text color="primary" class="mt-3" :to="{ name: 'SacTemplateCreation' }"
              >Go to Template Editor</v-btn
            >
          </div>
          <div
            v-else
            style="display: flex; flex-direction: column; gap: 8px; max-height: 380px; overflow-y: auto;"
          >
            <div
              v-for="tmpl in templates"
              :key="tmpl.id"
              style="display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; border: 1px solid #e8eaf6; border-radius: 10px; gap: 12px;"
            >
              <div style="flex: 1; min-width: 0;">
                <div style="font-weight: 600; font-size: 1rem; color: #263238;">
                  {{ tmpl.name }}
                </div>
                <div
                  v-if="tmpl.description"
                  style="font-size: 0.88rem; color: #78909c; margin-top: 2px;"
                >
                  {{ tmpl.description }}
                </div>
                <div style="font-size: 0.9rem; color: #78909c; margin-top: 4px;">
                  <v-icon size="21" class="mr-1">mdi-account-outline</v-icon
                  >{{ tmpl.createdBy }} &middot;
                  <v-icon size="21" class="mr-1">mdi-calendar-outline</v-icon
                  >{{ tmpl.createdAt ? tmpl.createdAt.slice(0, 10) : '' }}
                </div>
              </div>
              <v-btn small depressed color="primary" @click="loadFromTemplate(tmpl)">
                <v-icon left small>mdi-download-outline</v-icon>Load
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import securityAssuranceCaseApi from '../api/securityAssuranceCaseApi';
import sacTemplateApi from '../api/sacTemplateApi';

const CAE_TEMPLATE = {
  claims: [
    {
      id: 'CLM-001',
      title: 'Secure by design',
      description: 'The product implements security-by-design principles throughout its lifecycle.',
      status: 'Draft',
      subClaims: [
        {
          id: 'SUB-001',
          title: 'No known exploitable vulnerabilities',
          description: 'The product shall be delivered without known exploitable vulnerabilities.',
          status: 'Draft',
          evidences: [
            {
              id: '1',
              title: 'SBOM Analysis Report',
              type: 'Document',
              status: 'Approved',
              description: 'Software Bill of Materials confirming no CVEs above CVSS 7.0.',
            },
          ],
        },
        {
          id: 'SUB-002',
          title: 'Secure default configuration',
          description: 'Default configurations minimise attack surface.',
          status: 'Draft',
          evidences: [
            {
              id: '2',
              title: 'Security Configuration Baseline',
              type: 'Document',
              status: 'Pending',
              description: 'Documented hardened default settings according to CIS benchmarks.',
            },
          ],
        },
      ],
    },
    {
      id: 'CLM-002',
      title: 'Vulnerability management process',
      description: 'A documented process exists for handling discovered vulnerabilities.',
      status: 'Draft',
      subClaims: [
        {
          id: 'SUB-003',
          title: 'Responsible disclosure policy',
          description: 'A public point of contact for vulnerability reporting is available.',
          status: 'Draft',
          evidences: [
            {
              id: '3',
              title: 'Vulnerability Disclosure Policy Document',
              type: 'Policy',
              status: 'Approved',
              description: 'Published security.txt and VDP page.',
            },
          ],
        },
      ],
    },
  ],
};

export default {
  name: 'JsonView',
  components: {
    Breadcrumbs,
  },
  data() {
    return {
      jsonInput: '',
      isJsonValid: false,
      embedReady: false,
      syncTimeout: null,

      // Template library dialog
      templateDialog: false,
      templates: [],
      loadingTemplates: false,
    };
  },
  methods: {
    // ── Validate ──
    validateJson(val) {
      if (!val || !val.trim()) {
        this.isJsonValid = false;
        return;
      }
      try {
        JSON.parse(val);
        this.isJsonValid = true;
      } catch {
        this.isJsonValid = false;
      }
    },

    // ── Editor → JSONCrack ──
    onEditorInput() {
      this.validateJson(this.jsonInput);
      clearTimeout(this.syncTimeout);
      this.syncTimeout = setTimeout(() => {
        this.sendToEmbed();
      }, 400);
    },

    // ── Send current JSON to the embedded JSONCrack iframe ──
    sendToEmbed() {
      const iframe = document.getElementById('jsoncrackEmbed');
      if (!iframe || !iframe.contentWindow) {
        setTimeout(this.sendToEmbed, 500);
        return;
      }
      const options = { theme: 'light', direction: 'RIGHT' };
      const payload =
        this.jsonInput && this.isJsonValid ? this.jsonInput : JSON.stringify(CAE_TEMPLATE, null, 2);
      iframe.contentWindow.postMessage({ json: payload, options }, '*');
    },

    // ── Load template from saved library ──
    async loadTemplate() {
      this.templateDialog = true;
      this.loadingTemplates = true;
      try {
        const res = await sacTemplateApi.getAll();
        this.templates = res.data;
      } catch (err) {
        console.error('Error fetching templates:', err);
      } finally {
        this.loadingTemplates = false;
      }
    },

    loadFromTemplate(tmpl) {
      try {
        this.jsonInput = JSON.stringify(JSON.parse(tmpl.content), null, 2);
      } catch {
        this.jsonInput = tmpl.content;
      }
      this.validateJson(this.jsonInput);
      this.sendToEmbed();
      this.templateDialog = false;
    },

    loadExampleTemplate() {
      this.jsonInput = JSON.stringify(CAE_TEMPLATE, null, 2);
      this.validateJson(this.jsonInput);
      this.sendToEmbed();
    },
    formatJson() {
      try {
        const parsed = JSON.parse(this.jsonInput);
        this.jsonInput = JSON.stringify(parsed, null, 2);
        this.isJsonValid = true;
        this.sendToEmbed();
      } catch {
        // already invalid, leave as is
      }
    },
    clearJson() {
      this.jsonInput = '';
      this.isJsonValid = false;
      this.sendToEmbed();
    },
    updateJson() {
      this.sendToEmbed();
    },

    // ── Create CAE ──
    async handleCreateClick(jsonInput) {
      const id = this.$route.query.requirement?.id || null;
      try {
        const response = await securityAssuranceCaseApi.composeSac(id, JSON.parse(jsonInput));
        if (response && response.status === 201) {
          this.$router.push({ name: 'SacBuilder' });
        }
      } catch (error) {
        console.error('Error composing SAC:', error);
      }
    },
  },

  mounted() {
    // Load from localStorage if redirected from tree view
    const treeViewData = localStorage.getItem('treeViewJsonData');
    if (treeViewData) {
      try {
        const parsed = JSON.parse(treeViewData);
        this.jsonInput = JSON.stringify(parsed, null, 2);
        localStorage.removeItem('treeViewJsonData');
      } catch {
        this.jsonInput = '';
      }
    }

    this.validateJson(this.jsonInput);
    this.sendToEmbed();
  },

  beforeDestroy() {
    clearTimeout(this.syncTimeout);
  },
};
</script>

<style scoped>
/* ── Breadcrumbs ── */
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 24px;
}
.breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}

/* ── Root ── */
.jv-root {
  display: flex;
  flex-direction: column;
  gap: 0;
  height: calc(100vh - 160px);
  min-height: 600px;
}

/* ── Page Header ── */
.jv-page-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  border-radius: 14px 14px 0 0;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.jv-header-icon-wrap {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.jv-status-chip {
  background: rgba(255, 255, 255, 0.18) !important;
  color: white !important;
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
}
.jv-status-chip .v-icon {
  color: #69f0ae !important;
}

/* ── Main Layout ── */
.jv-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 0;
  background: #ffffff;
  border-radius: 0 0 14px 14px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* ── Panels ── */
.jv-left-panel {
  display: flex;
  flex-direction: column;
  width: 420px;
  min-width: 320px;
  flex-shrink: 0;
  border-right: 1px solid #e8eaf6;
}
.jv-right-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

/* ── Panel Headers ── */
.jv-panel-header {
  background: linear-gradient(135deg, #37474f 0%, #455a64 100%);
  padding: 10px 16px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* ── Banners ── */
.jv-error-banner {
  background: #fff3f3;
  border-bottom: 2px solid #ffcdd2;
  padding: 6px 14px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.jv-valid-banner {
  background: #f1f8f2;
  border-bottom: 2px solid #c8e6c9;
  padding: 6px 14px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* ── Editor ── */
.jv-editor {
  flex: 1;
  width: 100%;
  min-height: 0;
  resize: none;
  border: none;
  outline: none;
  font-family: 'Fira Mono', 'Menlo', 'Monaco', 'Consolas', monospace;
  font-size: 0.82rem;
  line-height: 1.6;
  padding: 14px 16px;
  background: #f8f9fc;
  color: #263238;
  box-sizing: border-box;
  overflow-y: auto;
}
.jv-editor::placeholder {
  color: #b0bec5;
  font-style: italic;
}

/* ── Action Bar ── */
.jv-action-bar {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-top: 1px solid #e8eaf6;
  background: #f5f7ff;
  flex-shrink: 0;
  gap: 8px;
}
.jv-btn {
  font-weight: 600 !important;
  letter-spacing: 0.02em !important;
  border-radius: 8px !important;
  text-transform: none !important;
}

/* ── Iframe Wrap ── */
.jv-iframe-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.jv-iframe {
  border: none;
  display: block;
  width: 100%;
  height: 100%;
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .jv-root {
    height: auto;
  }
  .jv-layout {
    flex-direction: column;
  }
  .jv-left-panel {
    width: 100%;
    min-width: 0;
  }
  .jv-editor {
    min-height: 220px;
    max-height: 340px;
  }
  .jv-iframe-wrap {
    height: 360px;
    flex: none;
  }
}
</style>
